import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EmployeePicker,
  createEmptySelection,
  type PickerSelection,
} from '@/components/employee-picker';
import { getEmployees } from '@/lib/employees';
import {
  getSpecificApprovers,
  getSubstituteApprovers,
  batchUpdateSpecificApprovers,
  batchUpdateSubstituteApprovers,
  type AbsenceTypeApprover,
} from '@/lib/absences';

interface ApproversSectionProps {
  absenceTypeId: string;
  type: 'specific' | 'substitute';
}

export function ApproversSection({ absenceTypeId, type }: ApproversSectionProps) {
  const queryClient = useQueryClient();
  const isSpecific = type === 'specific';

  // Fetch current approvers
  const { data: approvers, refetch: refetchApprovers } = useQuery({
    queryKey: ['absence-types', absenceTypeId, `${type}-approvers`],
    queryFn: () => (isSpecific ? getSpecificApprovers(absenceTypeId) : getSubstituteApprovers(absenceTypeId)),
  });

  // Convert API approvers to PickerSelection
  const initialSelection = useMemo((): PickerSelection => {
    if (!approvers) return createEmptySelection();

    return {
      employees: approvers.map((approver: AbsenceTypeApprover) => ({
        id: approver.employee!.id,
        firstName: approver.employee!.firstName,
        lastName: approver.employee!.lastName,
        preferredName: approver.employee?.preferredName,
        avatarUrl: approver.employee?.avatarUrl,
        workEmail: approver.employee!.workEmail,
        department: approver.employee?.department,
        position: approver.employee?.position,
      })),
      departments: [] as never[],
    };
  }, [approvers]);

  // Local state for picker
  const [selection, setSelection] = useState<PickerSelection>(createEmptySelection());
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoadingAll, setIsLoadingAll] = useState(false);

  // Sync initial selection when data loads
  const approversRef = JSON.stringify(approvers?.map((a) => a.employeeId) ?? []);
  useEffect(() => {
    if (approvers) {
      setSelection(initialSelection);
      setHasChanges(false);
    }
  }, [approversRef, initialSelection]);

  // Handle selection change
  const handleSelectionChange = (newSelection: PickerSelection) => {
    setSelection(newSelection);
    setHasChanges(true);
  };

  // Handle "Select All" - fetch all current staff and add them to selection
  const handleSelectAll = async () => {
    setIsLoadingAll(true);
    try {
      const response = await getEmployees({ statuses: ['active', 'probation'], limit: 1000, page: 1 });
      const allEmployees = response.data.map((emp) => ({
        id: emp.id,
        firstName: emp.firstName,
        lastName: emp.lastName,
        preferredName: emp.preferredName,
        avatarUrl: emp.avatarUrl,
        workEmail: emp.workEmail,
        department: emp.department,
        position: emp.position,
      }));

      setSelection({
        employees: allEmployees,
        departments: [] as never[],
      });
      setHasChanges(true);
    } finally {
      setIsLoadingAll(false);
    }
  };

  // Save mutation - batch update approvers
  const saveMutation = useMutation({
    mutationFn: async (employeeIds: string[]) => {
      const batchUpdateFn = isSpecific
        ? batchUpdateSpecificApprovers
        : batchUpdateSubstituteApprovers;
      return batchUpdateFn(absenceTypeId, employeeIds);
    },
    onSuccess: () => {
      refetchApprovers();
      queryClient.invalidateQueries({ queryKey: ['absence-types', absenceTypeId] });
      queryClient.invalidateQueries({ queryKey: ['absence-types'] });
      setHasChanges(false);
    },
  });

  // Save all changes
  const handleSave = async () => {
    const employeeIds = selection.employees.map((e) => e.id);
    await saveMutation.mutateAsync(employeeIds);
  };

  const isSaving = saveMutation.isPending;

  return (
    <EmployeePicker
      value={selection}
      onChange={handleSelectionChange}
      onSave={handleSave}
      isSaving={isSaving}
      hasChanges={hasChanges}
      showSaveButton={true}
      saveButtonText={`Save ${isSpecific ? 'Specific' : 'Substitute'} Approvers`}
      onSelectAll={handleSelectAll}
      isLoadingAll={isLoadingAll}
    />
  );
}
